import { productGidsMatch } from '@/lib/shopify/gid';
import { formatPrice, toCents, toDollars } from '@/utils/utilities.mjs';
import {
  applyPercentageDiscount,
  applyFixedAmountDiscount,
  cartLineFullPrice,
  cartSavings as sharedCartSavings,
  cartSubtotal as sharedCartSubtotal,
  cartTotal as sharedCartTotal,
  isCampaignActive,
  variantFullPrice,
} from './sharedStrategy';

// Re-export shared primitives so callers that import from this module
// continue to receive the correct implementations.
export { cartLineFullPrice, variantFullPrice };

export const variantSalePrice = (variant, discountSettings) => {
  const settings = discountSettings?.shopify_single_discount_code;
  const fullPrice = variantFullPrice(variant);
  const productId = variant?.product?.id;

  if (!settings || !isCampaignActive(discountSettings)) {
    return fullPrice;
  }

  if (productIdIsExcludedFromDiscounts(productId, discountSettings)) {
    return fullPrice;
  }

  switch (settings.type) {
    case 'percentage':
      return applyPercentageDiscount(fullPrice, settings.number);

    case 'fixed_amount':
      return applyFixedAmountDiscount(fullPrice, settings.number);

    default:
      console.error(`Unknown discounting type: ${settings.type}`);

      return fullPrice;
  }
};

export const cartLineSalePrice = (cartLine, discountSettings) => {
  const settings = discountSettings?.shopify_single_discount_code;
  const fullPrice = cartLineFullPrice(cartLine);

  if (!settings || !isCampaignActive(discountSettings)) {
    return fullPrice;
  }

  const productId = cartLine?.merchandise?.product?.id;

  if (productIdIsExcludedFromDiscounts(productId, discountSettings)) {
    return fullPrice;
  }

  // Trust Shopify line total when it doesn't match the computed full price.
  // Bundles use Shopify's pre-computed line total; campaign discounts are intentionally
  // NOT applied on top — the bundle price IS the sale price.
  const lineTotal = Number(cartLine.cost.totalAmount.amount);

  if (toCents(lineTotal) !== toCents(fullPrice)) {
    return lineTotal;
  }

  // Compute unit sale price directly from settings already resolved above,
  // avoiding a second exclusion check via variantSalePrice.
  const unitFullPrice = variantFullPrice(cartLine.merchandise);

  switch (settings.type) {
    case 'percentage':
      return toDollars(toCents(applyPercentageDiscount(unitFullPrice, settings.number)) * (cartLine.quantity ?? 1));

    case 'fixed_amount':
      return toDollars(toCents(applyFixedAmountDiscount(unitFullPrice, settings.number)) * (cartLine.quantity ?? 1));

    default:
      console.error(`Unknown discounting type: ${settings.type}`);
      return fullPrice;
  }
};

export const promotionalCopy = (discountSettings, variation) => {
  const settings = discountSettings?.shopify_single_discount_code;
  if (!settings || !isCampaignActive(discountSettings)) return null;

  const copy = discountSettings?.promotional_copy;

  return promotionalCopyForType(settings, copy, variation);
};

// Resolves settings and exclusion once for both code and message. custom_overlay_message
// is sitewide here, e.g. "[number] Off Sitewide" -> "20% Off Sitewide"; [scope] is always
// the literal "Sitewide", not read from the CMS.
export const overlayPromo = (discountSettings, variant = null) => {
  const NONE = { code: null, message: null };
  const settings = discountSettings?.shopify_single_discount_code;
  if (!settings || !isCampaignActive(discountSettings)) return NONE;
  if (productIdIsExcludedFromDiscounts(variant?.product?.id, discountSettings)) return NONE;

  const code = settings.discount_code ?? null;
  const numberStr =
    settings.type === 'percentage' ? `${settings.number}%` : formatPrice(settings.number, 'USD', true);
  const message = discountSettings?.custom_overlay_message
    ? discountSettings.custom_overlay_message.replaceAll('[number]', numberStr).replaceAll('[scope]', 'Sitewide')
    : (discountSettings?.promotional_copy?.compact
        ? replacePlaceholders(discountSettings.promotional_copy.compact, settings)
        : null);

  return { code, message };
};

const promotionalCopyForType = (settings, copy, variation) => {
  switch (variation) {
    case 'compact':
      return copy?.compact
        ? replacePlaceholders(copy.compact, settings)
        : null;
    case 'extended':
      return copy?.extended
        ? replacePlaceholders(copy.extended, settings)
        : null;
    case 'normal':
    default:
      return copy?.normal
        ? replacePlaceholders(copy.normal, settings)
        : null;
  }
};

const replacePlaceholders = (content, settings) => {
  const numberStr = settings.type === 'percentage' 
    ? `${settings.number}%` 
    : formatPrice(settings.number, 'USD', true);

  return content
    .replaceAll('[code]', settings.discount_code)
    .replaceAll('[number]', numberStr);
};

export const cartSubtotal = (cart, discountSettings) =>
  sharedCartSubtotal(cart, cartLineFullPrice);

export const cartTotal = (cart, discountSettings) =>
  sharedCartTotal(cart, discountSettings, cartLineSalePrice);

export const cartSavings = (cart, discountSettings) =>
  sharedCartSavings(cart, discountSettings, cartLineFullPrice, cartLineSalePrice);

function productIdIsExcludedFromDiscounts(productId, discountSettings) {
  if (productId == null) {
    return false;
  }

  const excludedProducts =
    discountSettings?.excluded_products ??
    discountSettings?.shopify_single_discount_code?.excluded_products ??
    [];

  return excludedProducts.some((product) =>
    productGidsMatch(product.shopify_product_id, productId),
  );
}
